<template>
  <v-container fluid class="pa-0" style="height: inherit; background-color: white;">

    <v-progress-linear v-if="loading" indeterminate absolute>
    </v-progress-linear>

    <template v-if="hasMissingTimeAppointments">
      <v-alert type="warning" class="mb-0 rounded-0" border="left" elevation="0" dense>
        {{ apointmentsMissingTime.length }} appointments are missing time
      </v-alert>
    </template>

    <template v-if="!loading">
      <UpgradedTable :tableStorageKey="tableStorageKey" :height="tableHeight" :defaultColumns="defaultColumns"
        :baseItemHeight="60" :groupBy="groupBy" :allowColumnConfig="true" :getSummariesFunc="getSummaries"
        :loading="loading" :rows="timeRows" :error="error">

        <template v-slot:empty-content>
          <EmptyPage :icon="'mdi-palm-tree'" :title="'No time registered'"
            :message="'No time registered for the period ' + i18nFormatDate(reportFilter.FromDate) + ' - ' + i18nFormatDate(reportFilter.ToDate) + '.'"
            class="ignoreodd">
            <template v-slot:actions>
              <slot name="empty-filtered-list-actions" />
            </template>
          </EmptyPage>
        </template>

        <!-- grouping header -->
        <template v-slot:group-header="{ row }">
          <template v-if="groupBy === 'EmploymentTimeReportDate'">
            {{ i18nFormatDate(row.__groupKey) }}
          </template>
        </template>

        <!-- Columns -->
         <template v-slot:header-EmploymentTimeReportDate>
          <div class="py-1">{{ $t('servicesystem.common_objects.commonterms.date') }}</div>
        </template>
        <template v-slot:EmploymentTimeReportDate="{ row }">
          {{ i18nFormatDate(row.EmploymentTimeReportDate) }}
        </template>

        <template v-slot:ParentJobId="{ row }">
          <router-link v-if="row.JobId" class="onlyshowlinkonhover"
            :to="{ name: 'ViewJob', params: { filter: 'my', jobId: row.ParentJobId } }">
            {{ row.ParentOrderId }}
          </router-link>
          <div v-else>
            {{ row.OrderId }}
          </div>
        </template>

        <template v-slot:CustomerId="{ row }">
          <router-link class="onlyshowlinkonhover"
            :to="{ name: 'ViewCustomer', params: { customerId: row.CustomerId } }">
            {{ row.CustomerId }} {{ row.CustomerName }}
          </router-link>
        </template>

        <template v-slot:JobId="{ row }">
          <router-link v-if="row.JobId" class="onlyshowlinkonhover" :to="{
            name: 'JobEditOrderLine',
            params: {
              filter: 'my',
              jobId: row.ParentJobId,
              machineJobId: row.JobId,
              lineId: row.Id,
            },
          }">
            {{ row.JobDisplayName }}
          </router-link>
        </template>

        <template v-slot:StartDateTime="{ row }">
          {{ i18nFormatTime(row.StartDateTime) }}
        </template>

        <template v-slot:StopDateTime="{ row }">
          {{ i18nFormatTime(row.StopDateTime) }}
        </template>

      <template v-slot:ArticleCount="{ row }">
          {{ (Math.round(row.ArticleCount * 100) / 100).toFixed(2) }}
        </template>

        <template v-slot:Duration="{ row }">
          {{ formatHoursToHHMM(row.ArticleCount) }}
        </template>

        <template v-slot:Actions="{ row }">
          <v-btn v-if="!row.ReadOnly" icon @click="editInDialog(row)">
            <v-icon size="20">mdi-pencil</v-icon>
          </v-btn>
          <v-btn v-else icon color="disabled" disabled>
            <v-icon size="20">mdi-lock</v-icon>
          </v-btn>
        </template>

      </UpgradedTable>
    </template>

    <EditTimeRowDialog :externaltrigger="dialogTrigger" :item="dialogItem" :articles="articles"
      :employmentList="employmentList" @Add="handleAdd" @Update="handleUpdate" @Delete="handleDelete">
    </EditTimeRowDialog>

  </v-container>
</template>


<script>
import clientAPI from "@/api/ClientAPIMixin.js";

import EmptyPage from "@/components/Sections/EmptyPage.vue";
import EditTimeRowDialog from "@/components/Dialogs/EditTimeRowDialog.vue";
import Vue from 'vue';
import UpgradedTable from './UpgradedTable.vue';

export default {
  name: "TimeReportTable",
  props: {
    tableStorageKey: String,
    height: Number,
    reportFilter: Object,
    defaultColumns: Object,
    groupBy: String,
  },
  components: {
    EmptyPage,
    EditTimeRowDialog,
    UpgradedTable,
  },
  mixins: [clientAPI],

  data() {
    return {
      loading: false,
      timeRows: [],
      apointmentsMissingTime: null,
      error: null,

      dialogTrigger: 1,
      dialogItem: null,

      employmentList: null,
      defaultArticle: null,
      articles: null,

      totalHours: 0,
      totalHoursNumber: 0,
    };
  },

  mounted() {
    this.getByPreset();
    this.getEmployments();
  },

  methods: {
    handleAdd(row) {
      //if new row is outside the date range, dont add it to the list
      if (this.comparedatetime(row.EmploymentTimeReportDate, this.reportFilter.ToDate)
        || this.comparedatetime(this.reportFilter.FromDate, row.EmploymentTimeReportDate)) {
        return;
      }
      //else add the row to the view
      else {
        this.timeRows.unshift(row);
        this.setTotalHours();
        console.log("Row added");
      }
    },

    handleUpdate(row) {
      //if updated row is outside the date range, remove it from the list
      if (this.comparedatetime(row.EmploymentTimeReportDate, this.reportFilter.ToDate)
        || this.comparedatetime(this.reportFilter.FromDate, row.EmploymentTimeReportDate)) {
        this.handleDelete(row);
      }
      //else update the row in the view
      else {
        var index = this.timeRows.findIndex((x) => x.Id === row.Id);
        if (index !== -1) {
          Vue.set(this.timeRows, index, row);
          this.setTotalHours();
          console.log("Row updated");
        }
        else {
          console.error("Row not found in timeRows", row, index);
        }
      }
    },

    handleDelete(row) {
      var index = this.timeRows.findIndex((x) => x.Id === row.Id);
      if (index !== -1) {
        this.timeRows.splice(index, 1);
        this.setTotalHours();
        console.log("Row removed");
      }
    },

    async getEmployments() {
      await clientAPI.methods.getEmploymentList()
        .then(data => {
          this.employmentList = data.map(e => {
            e.Id = e.Id.trimEnd();
            return e;
          });
        });
    },

    export() {
      clientAPI.methods.exportTimeReport(this.reportFilter);
    },

    editInDialog(row) {
      this.dialogItem = row;
      this.dialogTrigger++;
    },

    async getByPreset() {
      if (!this.reportFilter.ToDate || !this.reportFilter.FromDate) {
        return;
      }
      this.loading = true;
      this.error = null;

      await clientAPI.methods.getTimeReport(this.reportFilter)
        .then((response) => {
          this.timeRows = response.TimeRows;
          this.apointmentsMissingTime = response.AppointmentsMissingTimeRows;
          this.articles = response.TimeArticleOptions;
          this.setTotalHours();
          this.tableKey++;
        })
        .catch((error) => {
          this.error = error;
          console.error("Error fetching data:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    setTotalHours() {
      const values = this.timeRows.map(item => Number(item['ArticleCount']));
      var sum = values.reduce((prev, curr) => {
        const value = Number(curr);
        if (!isNaN(value)) {
          return prev + curr;
        } else {
          return prev;
        }
      }, 0);
      this.totalHoursNumber = Math.round(sum * 100) / 100;
      this.totalHours = this.totalHoursNumber.toFixed(2)
    },

    getSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      if (!data || data.length === 0) {
        return sums;
      }
      else {
        columns.forEach((column, index) => {
          if (index === 0) {
            sums[index] = this.$t('servicesystem.common_objects.time_report.total_hours');
            return;
          }
          else if (column.property === 'ArticleCount') {
            sums[index] = this.totalHours;
          }
          else if (column.property === 'Duration') {
            sums[index] = this.formatHoursToHHMM(this.totalHoursNumber);
          }
          else {
            sums[index] = ' ';
          }
        });

        return sums;
      }
    },

    formatHoursToHHMM(hours) {
      const n = Number(hours);
      if (isNaN(n)) return '';
      const totalMinutes = Math.round(n * 60);
      const hh = Math.floor(totalMinutes / 60);
      const mm = totalMinutes % 60;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    },

  },

  computed: {
    tableHeight() {
      return this.height - (this.hasMissingTimeAppointments ? 40 : 0);
    },

    hasMissingTimeAppointments() {
      return this.apointmentsMissingTime && this.apointmentsMissingTime.length > 0;
    },

    hasTimeRows() {
      return this.timeRows && this.timeRows.length > 0;
    },

    externalNewTrigger() {
      return this.$store.state.triggerevents.newTrigger;
    },

    externalExportTrigger() {
      return this.$store.state.triggerevents.exportTrigger;
    },

    filterJson() {
      return JSON.stringify(this.reportFilter);
    },
  },

  watch: {
    filterJson: {
      handler: function () {
        this.getByPreset();
      },
      immediate: true
    },

    externalNewTrigger: function () {
      this.editInDialog(null);
    },

    externalExportTrigger: function () {
      this.export();
    },
  },
};
</script>


<style lang='scss'>
.onlyshowlinkonhover:not(:hover) {
  text-decoration: none;
}
</style>